import React, {
	useMemo,
	useRef,
	useState,
	useEffect,
	useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Virtuoso } from 'react-virtuoso';
import {
	setRoomMessagesSeen,
	clearChatRoomMessages,
	loadChatRoomMessages,
	setSelectedRoom,
} from '../../stores/actions/chat';
import { ChatPost, ChatRoom } from '../../stores/reducers/chat';
import Socket, { ConnectedUser } from '../../connection/socket';
import ChatLink from './chat-link';
import ChatMessage from './chat-message';
import * as Utils from '../../utils/utils';
import Icon, { ICONS, COLORS } from '../general-components/icon';
import { random } from '../../utils/random';
import * as NotificationBuilder from '../../utils/notifications-builder';
import usePrevious from '../../utils/use-previous';
import useScrolledToBottom from '../../hooks/use-scrolled-to-bottom';
import useChat from '../../hooks/use-chat';
import * as Storage from '../../utils/local-storage';
import * as ChatApi from '../../connection/chat';
import { TABS } from '../../stores/types/tabs';
import InviteBadge from './invite-badge';
import ChatInput from './chat-input';
import colors from '../../utils/colors';
import LoadingSpinner from '../general-components/loading-spinner';
import { GreenroomState as GS } from '../../stores/types/types';

interface Props {
	socket: Socket | null;
	inputText: string;
	setInputText: (text: string) => void;
}

function extractUserFromInvite(user: any): any {
	return user.profile || user.gr_user;
}

export default function ChatRoomComponent(props: Props) {
	const {
		socket,
		inputText,
		setInputText,
	} = props;

	const {
		SessionReducer: {
			user,
			token,
			meeting,
			connectedUsers,
		},
		TabsReducer: {
			activeTab,
		},
		ChatReducer: {
			selectedRoom,
			rooms,
		},
		InvitationsReducer: {
			invitations,
		},
	} = useSelector((s: GS) => s);

	const dispatch = useDispatch();

	const { setChatNotificationsSeen } = useChat();
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	const currentRoom = useMemo(() => rooms
		.find((room: ChatRoom) => room.id === selectedRoom), [selectedRoom, rooms, rooms.length]);

	const usersFromMessages = useMemo(() => currentRoom?.messages
		.map((mess: ChatPost) => mess.sender) ?? [], [currentRoom]);

	const possibleUsers = useMemo(() => Utils.uniqueArray('gr_user', [
		...invitations.map(extractUserFromInvite).filter((profile: any) => !!profile.gr_user),
		...usersFromMessages,
		...connectedUsers,
	]), [connectedUsers, usersFromMessages, invitations]);

	const participants = useMemo(() => (currentRoom?.id === 0
		? possibleUsers
		: possibleUsers.filter((connUser: ConnectedUser) => currentRoom?.participants
			.includes(connUser.gr_user))), [currentRoom, possibleUsers]);

	const [mentionedUsers, setMentionedUsers] = useState<ConnectedUser[]>([]);
	const container = useRef<HTMLDivElement | null>(null);

	const prevRoomMessages = usePrevious(currentRoom?.messages);
	const prevActiveTab = usePrevious(activeTab);
	const { scrolledToBottom } = useScrolledToBottom({ scrolledContainerRef: container?.current });

	async function fetchData(lastId?: number) {
		if (token && meeting && currentRoom?.id !== undefined && currentRoom?.id !== null) {
			try {
				const res = await ChatApi.LoadRoomMessages(
					token,
					meeting?.meeting,
					currentRoom.id,
					lastId,
				);
				if (!res) {
					console.error('unable to fetch room messages');
					return;
				}
				dispatch(loadChatRoomMessages(res));
			} catch (e) {
				console.error(`unable to fetch room messages due to ${(e as Error).message}`);
			}
		}
	}

	const loadMoreMessages = useCallback(async () => {
		if (isFetchingMore || !token || !currentRoom?.id === undefined
			|| currentRoom?.id === null || !user || !meeting?.meeting) return;
		setIsFetchingMore(true);
		if (currentRoom?.messages.length) {
			await fetchData(currentRoom?.messages[currentRoom.messages.length - 1].id);
		}
		setIsFetchingMore(false);
	}, [meeting?.meeting, currentRoom?.messages, token, user, isFetchingMore]);

	const orderedMessages = useMemo(() => {
		if (!currentRoom?.messages) return [];

		return [...currentRoom.messages].reverse().map((msg, index, arr) => {
			const prevMsg = arr[index - 1];
			const isChild = prevMsg?.sender?.gr_user === msg?.sender?.gr_user;

			return {
				...msg,
				isChild,
			};
		});
	}, [currentRoom, currentRoom?.messages, currentRoom?.messages?.length]);

	async function sendMessage(messageText: string) {
		if (
			!messageText
			|| !token
			|| !user
			|| !meeting
			|| !currentRoom
		) {
			return;
		}

		const message = {
			id: 0,
			sender: user,
			meeting: meeting.meeting,
			room: currentRoom.id,
			created: Date.now(),
			message: messageText,
			seen: false,
			attachment: undefined,
			randomId: random(20),
		};

		message.message = message.message.replace(/\n/g, '\n\n');

		// Do not send blank messages
		if (!message.message.trim()) return;

		// send chat to API, make sure it's successful before sending it to other user in room
		try {
			const res = await ChatApi.PostChat(
				token,
				meeting.meeting,
				messageText,
				undefined,
				currentRoom?.id,
				undefined,
			);
			if (!res) {
				console.error('unable to create direct chat message');
				return;
			}
			message.id = res.insertId;
		} catch (e) {
			console.error(`unable to create chat room message due to ${(e as Error).message}`);
			return;
		}

		// only send the socket message if the user's socket message is correct
		if (socket) {
			socket.sendRoomChat(`room.${meeting.meeting}.${currentRoom.id}`, message);
		}

		if (mentionedUsers.length > 0) {
			NotificationBuilder.commentMentionNotification(token, user, mentionedUsers, meeting);
		} else {
			NotificationBuilder.commentBasicNotification(token, user, participants, meeting);
		}

		setMentionedUsers([]);
	}

	function goBack() {
		dispatch(setSelectedRoom(null));
	}

	useEffect(() => {
		const roomNumber = currentRoom?.id;
		const messages: number[] = currentRoom?.messages.reduce((acc: number[], cur?: ChatPost) => {
			if (cur?.id && (!cur.seen || cur.isNew)) {
				return [...acc, cur.id];
			}

			return acc;
		}, []) ?? [];

		if (
			token
			&& meeting?.meeting
			&& roomNumber !== undefined
			&& messages?.length
			&& activeTab === 'chat'
		) {
			dispatch(
				setRoomMessagesSeen(
					token,
					meeting.meeting,
					roomNumber,
					messages,
				),
			);
		}

		const highestMessageId: number | undefined = currentRoom?.messages.length
			? currentRoom?.messages.map((message) => message.id).sort((a, b) => b - a)[0]
			: undefined;

		if (highestMessageId && meeting?.meeting && roomNumber !== undefined && user?.gr_user) {
			const currentChatRoomsStatus = Storage.getStorageObject(
				`chat-rooms-last-seen-${meeting?.meeting}-${user.gr_user}`,
			);

			Storage.setStorageObject(`chat-rooms-last-seen-${meeting?.meeting}-${user.gr_user}`, {
				...currentChatRoomsStatus,
				[`${roomNumber}`]: highestMessageId,
			});
		}
	}, [currentRoom, currentRoom?.messages, activeTab, meeting, setRoomMessagesSeen, token]);

	const scrollToBottom = () => {
		// scroll to bottom of the chat window if the user:
		// - previously scrolled to the bottom and a new message is sent or received
		// - entered a new chat room
		// - changed tabs
		if (container.current
			&& ((prevRoomMessages?.length !== currentRoom?.messages.length && scrolledToBottom)
				|| !prevRoomMessages || prevActiveTab !== TABS.CHAT)) {
			container.current.scrollTo({ top: container.current.scrollHeight, behavior: 'auto' });
		}
	};

	// resizeObserver is used to observe the final size of the container after it finishes
	// animating in, so we can then scroll to the bottom (if necessary).
	const resizeObserver = new ResizeObserver(() => { scrollToBottom(); });

	useEffect(() => {
		if (container.current) {
			resizeObserver.observe(container.current);

			setTimeout(() => {
				// we are only observing container resizes in order to scroll to the bottom
				// after it animates in, so disconnect the observer shortly after.
				if (container.current) { resizeObserver.disconnect(); }
			}, 500);
		}
		return () => { if (container.current) { resizeObserver.disconnect(); } };
	}, []);

	useEffect(() => {
		scrollToBottom();
		setChatNotificationsSeen();
	}, [currentRoom?.messages, activeTab]);

	useEffect(() => {
		if (!token || !meeting?.meeting) return;

		const fetchMessages = async () => {
			if (currentRoom?.id) {
				dispatch(clearChatRoomMessages(currentRoom?.id, true));
			}
			setIsLoading(true);

			try {
				await fetchData();
			} catch (error: any) {
				console.error(error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchMessages();

		// Cleanup
		// eslint-disable-next-line consistent-return
		return () => {
			if (currentRoom?.id) {
				dispatch(clearChatRoomMessages(currentRoom?.id));
			}
		};
	}, []);

	function getBadge(room: ChatRoom): string {
		if (room.name === 'Presenter chat') {
			const possUsers = [...possibleUsers];
			const sendersFromMessages = room?.messages.map((mess: ChatPost) => mess.sender) ?? [];

			const allParticipants = Utils.uniqueArray('email', [
				...sendersFromMessages,
				...possUsers,
			]);

			return `${allParticipants.length} ${(allParticipants.length) !== 1 ? 'Members' : 'Member'}`;
		}
		const sendersFromMessages = room?.messages.map((mess: ChatPost) => mess.sender?.gr_user) ?? [];
		const totalLength = new Set([...room.participants, ...sendersFromMessages]).size;

		return `${totalLength} ${(totalLength) !== 1 ? 'Members' : 'Member'}`;
	}

	return (
		<div className="room-wrapper display-flex flex-flow-column height-full">
			<div>
				<button className="chat-back-button" onClick={goBack}>
					<Icon name={ICONS.ARROW_LEFT} color={COLORS.WHITE} size={17}></Icon>
					<span>All</span>
				</button>
			</div>
			{isLoading
				? <div className="notes-loading-container">
					<LoadingSpinner
						height={100}
						width={100}
						color={colors.red}
					/>
				</div>
				: <>
					<div className='chat-top height-fit-content max-height-half padding-bottom-medium'>
						{!!currentRoom && (
							<ChatLink
								room={currentRoom}
								icon={<Icon name={ICONS.PEOPLE} size={22} color={COLORS.BLACK} ></Icon>}
								badge={getBadge(currentRoom)}
								title={currentRoom.name}
								message={currentRoom.messages?.[0]}
								showParticipants={true}
								inRoom={true}
								socket={socket}
							>
								<div className="room-participants">
									{participants.map((participant: ConnectedUser) => (
										participant.gr_user !== user!.gr_user
											? <InviteBadge
												key={participant.gr_user}
												user={participant}
												style={2}
											/>
											: null
									))}
								</div>
							</ChatLink>
						)}
					</div>

					<div className="chat-container height-full">
						<Virtuoso
							style={{ height: '100%' }}
							data={orderedMessages || []}
							firstItemIndex={orderedMessages[0]?.id ?? 0}
							initialTopMostItemIndex={orderedMessages.length - 1}
							increaseViewportBy={200}
							startReached={loadMoreMessages}
							alignToBottom
							followOutput="auto"
							itemContent={(index, message: ChatPost) => (
								<ChatMessage
									key={message.id ?? index + (message.randomId || '')}
									post={message}
									user={message.sender}
									isChild={message.isChild}
									socket={socket}
								/>
							)}
						/>
					</div>
					{user && (
						<ChatInput
							user={user}
							onSendMessage={sendMessage}
							inputText={inputText}
							setInputText={setInputText}
						/>
					)}
				</>}
		</div>
	);
}
